import { AddAPhoto, Close } from '@mui/icons-material'
import { Box, Button, Grid, IconButton, Typography } from '@mui/material'
import { FormikHelpers } from 'formik'
import { type ChangeEvent, useEffect, useRef, useState } from 'react'

export const CHECK_IN_PHOTO_ACCEPT = 'image/png, image/jpeg'

export interface CheckInPhotoDialogNavProps {
  isFirstInSequence: boolean
  isLastInSequence: boolean
  onGoToPrevious: () => void
}

/** Stable submit behavior for SUBMIT vs SUBMIT & GO TO NEXT. */
export function useCheckInPhotoSubmitIntent<T>(onSave: (values: T, options?: { goToNext?: boolean }) => void) {
  const intentRef = useRef<'close' | 'next'>('close')
  const onSaveRef = useRef(onSave)
  onSaveRef.current = onSave

  const onFormSubmit = (values: T, formikHelpers: FormikHelpers<T>) => {
    const goToNext = intentRef.current === 'next'
    intentRef.current = 'close'
    onSaveRef.current(values, { goToNext })
    formikHelpers.setSubmitting(false)
  }

  const submitClose = async (submitForm: () => Promise<void>) => {
    intentRef.current = 'close'
    await submitForm()
  }

  const submitThenNext = async (submitForm: () => Promise<void>) => {
    intentRef.current = 'next'
    await submitForm()
  }

  return { onFormSubmit, submitClose, submitThenNext }
}

export function useObjectUrlPreview(file: File | undefined | null): string | undefined {
  const [url, setUrl] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (!file) {
      setUrl(undefined)
      return
    }
    const objectUrl = URL.createObjectURL(file)
    setUrl(objectUrl)
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [file])

  return url
}

export function CheckInPhotoPreview({
  alt,
  file,
  placeholderSrc,
  serverSrc
}: {
  alt: string
  file?: File | null
  placeholderSrc: string
  serverSrc: string
}) {
  const objectUrl = useObjectUrlPreview(file ?? undefined)
  return <img alt={alt} src={objectUrl || serverSrc || placeholderSrc} style={{ width: '100%' }} />
}

function normalizeComparableValue(value: unknown) {
  return value == null ? null : value
}

/**
 * Avoid re-sending unchanged photo dialogs when an uploaded image already exists.
 * The backend update endpoints expect a new file for required photos, so revisiting
 * an already-saved dialog should simply close or advance unless the user changed something.
 */
export function shouldSubmitCheckInPhotoSave<T extends object>(
  initialValues: T,
  values: T,
  ignoredKeys: Array<keyof T> = []
) {
  const initialRecord = initialValues as Record<string, unknown>
  const valuesRecord = values as Record<string, unknown>
  const allKeys = new Set([...Object.keys(initialRecord), ...Object.keys(valuesRecord)])
  const ignoredKeySet = new Set<string>(ignoredKeys.map(key => String(key)))
  const fileKeys = [...allKeys].filter(key => key.endsWith('PhotoFile'))
  const documentGuidKeys = [...allKeys].filter(key => key === 'documentGuid')

  const hasNewFile = fileKeys.some(key => Boolean(valuesRecord[key]))
  if (hasNewFile) {
    return true
  }

  const hasExistingUploadedPhoto = documentGuidKeys.some(key => Boolean(valuesRecord[key] ?? initialRecord[key]))
  if (!hasExistingUploadedPhoto) {
    return true
  }

  return [...allKeys]
    .filter(key => !fileKeys.includes(key) && !documentGuidKeys.includes(key) && !ignoredKeySet.has(key))
    .some(key => normalizeComparableValue(initialRecord[key]) !== normalizeComparableValue(valuesRecord[key]))
}

export function useLifecycleDocumentPreview(
  vehicleGuid: string | undefined,
  documentGuid: string | undefined,
  displayFileName: string | undefined,
  download: (vehicleGuid: string, documentGuid: string) => Promise<string>
): string {
  const [src, setSrc] = useState<string>('')

  useEffect(() => {
    if (!documentGuid || !vehicleGuid) {
      setSrc('')
      return
    }

    const extension = displayFileName?.split('.').pop() ?? 'jpeg'
    download(vehicleGuid, documentGuid)
      .then(base64 => {
        setSrc(`data:image/${extension};base64,${base64}`)
      })
      .catch(() => {
        setSrc('')
      })
  }, [documentGuid, displayFileName, download, vehicleGuid])

  return src
}

export function CheckInPhotoDialogTitleRow({ onClose, title }: { onClose: () => void; title: string }) {
  return (
    <Grid item xs={12}>
      <Box alignItems='flex-start' display='flex' justifyContent='space-between'>
        <Typography component='div' variant='h6'>
          {title}
        </Typography>
        <IconButton aria-label='Close' edge='end' onClick={onClose} size='small'>
          <Close />
        </IconButton>
      </Box>
    </Grid>
  )
}

export function CheckInPhotoBrowseTakeRow({
  browseInputId,
  cameraInputId,
  disabled,
  onFileChosen
}: {
  browseInputId: string
  cameraInputId: string
  disabled?: boolean
  onFileChosen: (file: File) => void
}) {
  const onFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      onFileChosen(e.target.files[0])
    }
    e.target.value = ''
  }

  return (
    <>
      <Grid item xs={12}>
        <input
          accept={CHECK_IN_PHOTO_ACCEPT}
          hidden
          id={browseInputId}
          type='file'
          onChange={onFileInputChange}
        />
        <Button
          color='primary'
          disabled={disabled}
          fullWidth
          onClick={() => document.getElementById(browseInputId)?.click()}
          variant='outlined'
        >
          BROWSE
        </Button>
      </Grid>
      <Grid item xs={12}>
        <input
          accept={CHECK_IN_PHOTO_ACCEPT}
          capture='environment'
          hidden
          id={cameraInputId}
          type='file'
          onChange={onFileInputChange}
        />
        <Button
          color='primary'
          disabled={disabled}
          endIcon={<AddAPhoto />}
          fullWidth
          onClick={() => document.getElementById(cameraInputId)?.click()}
          variant='outlined'
        >
          TAKE PHOTO
        </Button>
      </Grid>
    </>
  )
}

export function CheckInPhotoStackedNavButtons({
  isLoading,
  isSubmitting,
  nav,
  submitClose,
  submitForm,
  submitThenNext
}: {
  isLoading: boolean
  isSubmitting: boolean
  nav?: CheckInPhotoDialogNavProps
  submitClose: (submitForm: () => Promise<void>) => Promise<void>
  submitForm: () => Promise<void>
  submitThenNext: (submitForm: () => Promise<void>) => Promise<void>
}) {
  return (
    <>
      <Grid item xs={12}>
        <Button
          color='primary'
          disabled={isSubmitting || isLoading}
          fullWidth
          onClick={() => void submitClose(submitForm)}
          type='button'
          variant='contained'
        >
          SUBMIT
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button
          color='primary'
          disabled={isSubmitting || isLoading || nav?.isLastInSequence}
          fullWidth
          onClick={() => void submitThenNext(submitForm)}
          type='button'
          variant='contained'
        >
          SUBMIT & GO TO NEXT
        </Button>
      </Grid>
      <Grid item xs={12}>
        <Button
          color='primary'
          disabled={isSubmitting || isLoading || nav?.isFirstInSequence}
          fullWidth
          onClick={nav?.onGoToPrevious}
          type='button'
          variant='outlined'
        >
          PREVIOUS
        </Button>
      </Grid>
    </>
  )
}
